// src/utils/generateAnexoPDF.js
// DGA "Anexo a la Declaración de Mercancías para Pequeños Envíos" — browser-side PDF generator.
// Uses pdf-lib (dynamic import). Takes supabaseClient as param — never imports supabase directly.

const COLS_W = [80, 35, 30, 190, 25, 25, 50, 60, 57]; // total = 552pt
const COLS_H = ['', 'No.De PK', 'Cant.', 'Descripción', '', '', 'Valor $ Unit.', 'Total', 'IVA 30%'];
const MARGIN = 30;
const PAGE_W = 612;
const PAGE_H = 792;
const MAX_ROWS_PER_PAGE = 45;
const ROW_H = 12;
const COL_HDR_Y = 660; // pdf-lib native (y=0 at bottom)
const SUB_HDR_Y = 650;        // Usado/Nuevo sub-header row (COL_HDR_Y - 10)
const HDR_BORDER_Y = 644;     // bottom border of column-header band
const FIRST_ROW_Y = 636;      // first data row, clear of sub-header

// Pre-compute column x positions
const COL_X = [];
(function () {
  let x = MARGIN;
  for (const w of COLS_W) { COL_X.push(x); x += w; }
})();

function fmtDate(iso) {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function drawRightText(page, text, rightX, y, size, font, color) {
  const w = font.widthOfTextAtSize(text, size);
  page.drawText(text, { x: rightX - w, y, size, font, color });
}

// Truncado por ancho MEDIDO, no por conteo de caracteres: misma técnica que el
// recorte con elipsis de la columna del destinatario (ver el bucle del
// pkg_header), generalizada. Si el texto cabe se devuelve intacto; si no, se
// recorta carácter a carácter hasta que `texto + '…'` quepa en maxWidth.
export function ajustarDescripcion(text, font, size, maxWidth) {
  const str = String(text ?? '');
  if (font.widthOfTextAtSize(str, size) <= maxWidth) return str;
  let out = str;
  while (out.length > 1 && font.widthOfTextAtSize(out + '…', size) > maxWidth) {
    out = out.slice(0, -1);
  }
  return out + '…';
}

// ── Private helper: fetch, validate, resolve labels, sort, assign _seq ────────
async function _loadAndSortAnexo(manifestId, supabaseClient) {
  console.log('[Anexo] Fetching data for manifest:', manifestId);

  const [draftResult, configResult] = await Promise.all([
    supabaseClient
      .from('declaration_drafts')
      .select('payload, manifest_id, confirmed_at, total_fob')
      .eq('manifest_id', manifestId)
      .eq('status', 'confirmed')
      .maybeSingle(),
    supabaseClient
      .from('customs_config')
      .select('gestor_code, aduana_code, gestor_name, threshold_usd, dai_rate, iva_rate, registro_prefix')
      .limit(1)
      .maybeSingle(),
  ]);

  if (!draftResult.data) {
    throw new Error('No hay declaración confirmada para este manifiesto. Confirme la declaración primero.');
  }

  const draft = draftResult.data;
  const config = configResult.data || {};
  const packages = draft.payload?.packages;

  if (!packages || packages.length === 0) {
    throw new Error('La declaración no tiene paquetes. Verifique el contenido del manifiesto.');
  }

  // Resolve enc- labels → recipient names
  const encUuids = packages
    .filter(p => p.deck_key?.startsWith('enc-'))
    .map(p => p.deck_key.replace('enc-', ''));

  const encLabelMap = {};
  if (encUuids.length > 0) {
    const { data: encPkgs } = await supabaseClient
      .from('encomienda_packages')
      .select('id, recipient_name, unique_package_id')
      .in('id', encUuids);
    console.log('[Anexo] Resolved', (encPkgs || []).length, 'enc-labels');
    for (const ep of (encPkgs || [])) {
      encLabelMap[ep.id] = `${(ep.recipient_name || 'Sin nombre').toUpperCase()} - ${ep.unique_package_id || ep.id.slice(0, 8)}`;
    }
  }

  // Resolve ship- labels
  const shipUuids = packages
    .filter(p => p.deck_key?.startsWith('ship-'))
    .map(p => p.deck_key.replace('ship-', ''));
  let shipLabelMap = {};
  if (shipUuids.length > 0) {
    const { data: ships } = await supabaseClient
      .from('shipments')
      .select('id, recipient_name, unique_package_id, customer_id')
      .in('id', shipUuids);
    for (const s of (ships || [])) {
      const name = (s.recipient_name || s.customer_id || '').toUpperCase();
      const pkg  = s.unique_package_id || s.id.slice(0, 8);
      shipLabelMap[s.id] = name ? `${name} - ${pkg}` : pkg;
    }
    console.log('[Anexo] Ship labels resolved:', Object.keys(shipLabelMap).length);
  }

  // Sort by trailing number of the RESOLVED display label (LOCKED — do not change)
  const sortedPackages = [...packages].sort((a, b) => {
    const resolveLabel = (pkg) => {
      if (pkg.deck_key?.startsWith('enc-')) {
        return encLabelMap[pkg.deck_key.replace('enc-', '')] || pkg.label || '';
      }
      if (pkg.deck_key?.startsWith('ship-')) {
        return shipLabelMap[pkg.deck_key.replace('ship-', '')] || pkg.label || '';
      }
      return pkg.label || '';
    };
    const getNum = (pkg) => {
      const label = resolveLabel(pkg);
      const m = label.match(/(\d+)\s*$/);
      return m ? parseInt(m[1], 10) : 0;
    };
    const numDiff = getNum(a) - getNum(b);
    if (numDiff !== 0) return numDiff;
    return resolveLabel(a).localeCompare(resolveLabel(b));
  });

  // Assign stable _seq based on sort position — identical whether package ends up in main or individual PDF
  const pkgsWithSeq = sortedPackages.map((p, i) => ({ ...p, _seq: i + 1 }));

  return { draft, config, sortedPackages: pkgsWithSeq, encLabelMap, shipLabelMap };
}

// ── Scan helper — returns high-value package list for split decision ──────────
export async function scanAnexoHighValue(manifestId, supabaseClient) {
  try {
    const { config, sortedPackages, encLabelMap, shipLabelMap } = await _loadAndSortAnexo(manifestId, supabaseClient);
    const threshold = parseFloat(config.threshold_usd) || 200;

    const all = sortedPackages.map(pkg => {
      const displayLabel = pkg.deck_key?.startsWith('enc-')
        ? (encLabelMap[pkg.deck_key.replace('enc-', '')] || pkg.label || '')
        : pkg.deck_key?.startsWith('ship-')
          ? (shipLabelMap[pkg.deck_key.replace('ship-', '')] || pkg.label || '')
          : (pkg.label || '');

      // Bilingual fallback chain for both qty and unit value
      const total = (pkg.items || []).reduce((sum, item) => {
        const qty   = parseFloat(item.quantity    ?? item.cantidad    ?? 1)   || 1;
        const uVal  = parseFloat(item.unit_value  ?? item.valor_unitario ?? item.unit_value_usd ?? 0) || 0;
        return sum + qty * uVal;
      }, 0);

      const splitIdx = displayLabel.lastIndexOf(' - ');
      const recipient = splitIdx >= 0 ? displayLabel.slice(0, splitIdx) : displayLabel;

      return { seq: pkg._seq, label: displayLabel, total, recipient };
    });

    const highValue = all.filter(p => p.total >= threshold);
    return { threshold, all, highValue };
  } catch (err) {
    console.error('[Anexo split] scanAnexoHighValue error:', err);
    throw err;
  }
}

// ── Pure renderer ─────────────────────────────────────────────────────────────
// Takes already-loaded data and returns the PDF bytes. Extracted verbatim from
// generateAnexoPDF so a surface with a different data model (Desarrollos: flat
// items, no manifest, no declaration_drafts row) can render the same DGA layout
// without duplicating any of it.
//
// Input shape:
//   draft           { confirmed_at }             — only confirmed_at is read
//   config          { gestor_code, aduana_code, gestor_name, registro_prefix }
//   sortedPackages  [{ _seq, label, deck_key?, items: [{ description, quantity,
//                      unit_value, condition }], taxes: { dai } }]
//   encLabelMap / shipLabelMap  optional, only used when deck_key is present
//
// generateAnexoPDF below is unchanged in behavior — it loads, then calls this.
export async function renderAnexoPdf(
  { draft, config, sortedPackages, encLabelMap = {}, shipLabelMap = {} },
  // drawTaxColumn is opt-in and defaults OFF: the existing Maxi/Lazo Anexo
  // leaves the "IVA 30%" cells blank per row, and turning it on unconditionally
  // would change every PDF those tenants already produce.
  { skipSeqs = [], onlySeq = null, logLabel = '', drawTaxColumn = false } = {}
) {
  // Mutual exclusion: onlySeq wins
  let effectiveSkipSeqs = skipSeqs;
  if (onlySeq !== null && skipSeqs.length > 0) {
    console.warn('[Anexo split] both skipSeqs and onlySeq passed, using onlySeq');
    effectiveSkipSeqs = [];
  }

  try {
    // Filter packages based on mode
    let filteredPackages;
    if (onlySeq !== null) {
      filteredPackages = sortedPackages.filter(p => p._seq === onlySeq);
    } else if (effectiveSkipSeqs.length > 0) {
      filteredPackages = sortedPackages.filter(p => !effectiveSkipSeqs.includes(p._seq));
    } else {
      filteredPackages = sortedPackages;
    }

    if (filteredPackages.length === 0) {
      throw new Error('No hay paquetes para renderizar con los filtros indicados.');
    }

    // Build flat row list — uses pkg._seq instead of an incrementing counter
    const rows = [];
    for (const pkg of filteredPackages) {
      const displayLabel = pkg.deck_key?.startsWith('enc-')
        ? (encLabelMap[pkg.deck_key.replace('enc-', '')] || pkg.label || '')
        : pkg.deck_key?.startsWith('ship-')
          ? (shipLabelMap[pkg.deck_key.replace('ship-', '')] || pkg.label || '')
          : (pkg.label || '');
      // parseFloat, not parseInt: Desarrollos stores cantidad as NUMERIC(10,3)
      // and a decimal quantity would otherwise truncate on the printed Anexo.
      // Integer quantities render identically, so Maxi/Lazo are unaffected.
      const totalQty  = (pkg.items || []).reduce((s, i) => s + (parseFloat(i.quantity) || 1), 0);
      const taxTier   = (pkg.taxes?.dai > 0) ? '30%' : 'IVA';
      const itemCount = (pkg.items || []).length;

      const allItems = (pkg.items || []).map(item => {
        const qty       = parseFloat(item.quantity) || 1;
        const unitPrice = parseFloat(item.unit_value) || 0;
        return {
          description: (item.description || '').toUpperCase().slice(0, 60),
          qty,
          unitPrice,
          total: qty * unitPrice,
          condition: item.condition || 'NUEVO',
          taxTier,
          // Only read when drawTaxColumn is on; callers that don't set it are
          // unaffected.
          taxAmount: parseFloat(item.tax_amount) || 0,
        };
      });

      // pkg_header absorbs the first item; uses pkg._seq as stable sequence
      rows.push({
        type: 'pkg_header',
        seq: pkg._seq,
        label: displayLabel,
        totalQty,
        itemCount,
        firstItem: allItems[0] || null,
      });
      for (let i = 1; i < allItems.length; i++) {
        rows.push({ type: 'item', ...allItems[i] });
      }
      const pkgFob = allItems.reduce((s, i) => s + i.total, 0);
      rows.push({ type: 'pkg_subtotal', total: pkgFob });
    }

    // Package-atomic pagination
    const packageGroups = [];
    let currentGroup = [];
    for (const row of rows) {
      if (row.type === 'pkg_header' && currentGroup.length > 0) {
        packageGroups.push(currentGroup);
        currentGroup = [];
      }
      currentGroup.push(row);
    }
    if (currentGroup.length > 0) packageGroups.push(currentGroup);

    const pages = [];
    let currentPage = [];
    for (const group of packageGroups) {
      if (group.length > MAX_ROWS_PER_PAGE) {
        if (currentPage.length > 0) { pages.push(currentPage); currentPage = []; }
        let remaining = [...group];
        while (remaining.length > MAX_ROWS_PER_PAGE) {
          pages.push(remaining.splice(0, MAX_ROWS_PER_PAGE));
        }
        currentPage = remaining;
      } else if (currentPage.length + group.length > MAX_ROWS_PER_PAGE) {
        pages.push(currentPage);
        currentPage = [...group];
      } else {
        currentPage.push(...group);
      }
    }
    if (currentPage.length > 0) pages.push(currentPage);
    console.log('[Anexo] Paginated into', pages.length, 'pages,', packageGroups.length, 'packages.');

    const totalPages = pages.length;

    // Grand total — sum only the filtered packages' pkg_subtotal rows
    let grandTotal = 0;
    for (const row of rows) {
      if (row.type === 'pkg_subtotal') grandTotal += row.total;
    }

    // Render with pdf-lib
    const { PDFDocument, rgb, StandardFonts } = await import('pdf-lib');
    const pdfDoc   = await PDFDocument.create();
    const font     = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    const black    = rgb(0, 0, 0);
    const gray     = rgb(0.7, 0.7, 0.7);
    const darkGray = rgb(0.3, 0.3, 0.3);

    const fecha          = draft.confirmed_at ? fmtDate(draft.confirmed_at) : fmtDate(new Date().toISOString());
    const gestorCode     = config.gestor_code     || 'GE2662';
    const aduanaCode     = config.aduana_code     || '03';
    const gestorName     = config.gestor_name     || '';
    const registroPrefix = config.registro_prefix || '4-';

    for (let pageIdx = 0; pageIdx < totalPages; pageIdx++) {
      const pageRows = pages[pageIdx];
      const pageNum  = pageIdx + 1;
      const isLastPage = pageIdx === totalPages - 1;

      const page = pdfDoc.addPage([PAGE_W, PAGE_H]);

      // Title block
      const titleDefs = [
        { text: 'DIRECCION GENERAL DE ADUANAS',                              size: 9, f: fontBold },
        { text: 'DIVISION DE OPERACIONES',                                    size: 8, f: font     },
        { text: 'ANEXO A LA DECLARACIÓN DE MERCANCIAS PARA PEQUEÑOS ENVIOS Y DECLARACIÓN DE EQUIPAJE', size: 7, f: font },
      ];
      let titleY = PAGE_H - 20;
      for (const td of titleDefs) {
        const tw = td.f.widthOfTextAtSize(td.text, td.size);
        page.drawText(td.text, {
          x: MARGIN + (PAGE_W - 2 * MARGIN - tw) / 2,
          y: titleY, size: td.size, font: td.f, color: black,
        });
        titleY -= 11;
      }

      // Info block
      const infoY      = titleY - 4;
      const leftInfoX  = MARGIN + 10;
      const rightInfoX = PAGE_W / 2 + 20;

      const drawLabelValue = (label, value, x, y) => {
        page.drawText(label, { x, y, size: 7, font: fontBold, color: black });
        const lw     = fontBold.widthOfTextAtSize(label, 7);
        const valueX = x + lw + 4;
        page.drawText(value, { x: valueX, y, size: 7, font, color: black });
        page.drawLine({
          start: { x: valueX, y: y - 2 },
          end:   { x: valueX + 80, y: y - 2 },
          thickness: 0.5, color: black,
        });
      };

      const leftPairs = [
        ['No. de Registro de Mercancias:', registroPrefix],
        ['Fecha de Registro:',             fecha],
        ['Código de Aduana:',              aduanaCode],
      ];
      // No. de Hojas reflects filtered page count (individual → 1/1)
      const rightPairs = [
        ['No. de Hojas:',                    `${pageNum}/${totalPages}`],
        ['Código de Gestor de Encomiendas:', gestorCode],
      ];

      let leftY = infoY;
      for (const [label, value] of leftPairs) {
        drawLabelValue(label, value, leftInfoX, leftY);
        leftY -= 11;
      }
      let rightY = infoY;
      for (const [label, value] of rightPairs) {
        drawLabelValue(label, value, rightInfoX, rightY);
        rightY -= 11;
      }

      page.drawLine({
        start: { x: MARGIN, y: infoY - 36 },
        end:   { x: PAGE_W - MARGIN, y: infoY - 36 },
        thickness: 0.5, color: black,
      });

      page.drawLine({
        start: { x: MARGIN, y: COL_HDR_Y + 10 },
        end: { x: PAGE_W - MARGIN, y: COL_HDR_Y + 10 },
        thickness: 0.8, color: black,
      });

      // Column headers
      for (let ci = 0; ci < COLS_H.length; ci++) {
        const hdr = COLS_H[ci];
        if (!hdr) continue;
        const hw = fontBold.widthOfTextAtSize(hdr, 6);
        const hx = COL_X[ci] + Math.max(0, (COLS_W[ci] - hw) / 2);
        page.drawText(hdr, { x: hx, y: COL_HDR_Y, size: 6, font: fontBold, color: black });
      }

      page.drawLine({
        start: { x: MARGIN, y: HDR_BORDER_Y },
        end: { x: PAGE_W - MARGIN, y: HDR_BORDER_Y },
        thickness: 0.5, color: black,
      });

      page.drawLine({
        start: { x: COL_X[4], y: SUB_HDR_Y + 6 },
        end:   { x: COL_X[4] + COLS_W[4] + COLS_W[5], y: SUB_HDR_Y + 6 },
        thickness: 0.3, color: black,
      });

      const mercX     = COL_X[4];
      const mercW     = COLS_W[4] + COLS_W[5];
      const mercLabel = 'Mercancías';
      const mercLW    = fontBold.widthOfTextAtSize(mercLabel, 6);
      page.drawText(mercLabel, {
        x: mercX + (mercW - mercLW) / 2,
        y: COL_HDR_Y, size: 6, font: fontBold, color: black,
      });

      const usadoW = fontBold.widthOfTextAtSize('Usado', 5);
      page.drawText('Usado', {
        x: COL_X[4] + (COLS_W[4] - usadoW) / 2,
        y: COL_HDR_Y - 10, size: 5, font: fontBold, color: black,
      });
      const nuevoW = fontBold.widthOfTextAtSize('Nuevo', 5);
      page.drawText('Nuevo', {
        x: COL_X[5] + (COLS_W[5] - nuevoW) / 2,
        y: COL_HDR_Y - 10, size: 5, font: fontBold, color: black,
      });

      // Item rows
      let rowY        = FIRST_ROW_Y;
      let pageSubtotal = 0;

      for (const row of pageRows) {
        if (row.type === 'pkg_header') {
          const labelStr     = row.label || '';
          const splitIdx     = labelStr.lastIndexOf(' - ');
          const recipientName = splitIdx >= 0 ? labelStr.slice(0, splitIdx) : labelStr;
          const pkgCode      = splitIdx >= 0 ? labelStr.slice(splitIdx + 3).slice(0, 12) : '';

          const nameWithCount = `${recipientName} - ${row.itemCount ?? row.totalQty}`;
          const availW = COLS_W[0] - 4;
          let nameSize = 7;
          while (nameSize > 4 && fontBold.widthOfTextAtSize(nameWithCount, nameSize) > availW) {
            nameSize -= 0.5;
          }
          let nameText = nameWithCount;
          if (fontBold.widthOfTextAtSize(nameText, nameSize) > availW) {
            while (nameText.length > 1 && fontBold.widthOfTextAtSize(nameText + '…', nameSize) > availW) {
              nameText = nameText.slice(0, -1);
            }
            nameText = nameText + '…';
          }
          page.drawText(nameText, {
            x: COL_X[0] + 2, y: rowY, size: nameSize, font: fontBold, color: black,
          });

          const pkgNum = pkgCode.match(/(\d+)$/) ? pkgCode.match(/(\d+)$/)[1] : pkgCode;
          page.drawText(pkgNum, {
            x: COL_X[1] + 2, y: rowY, size: 7, font: fontBold, color: black,
          });

          const fi = row.firstItem;
          if (fi) {
            page.drawText(String(fi.qty), {
              x: COL_X[2] + 2, y: rowY, size: 6, font, color: black,
            });
            page.drawText(ajustarDescripcion(fi.description, font, 6, COLS_W[3] - 4), {
              x: COL_X[3] + 2, y: rowY, size: 6, font, color: black,
            });
            if (fi.condition !== 'NUEVO') {
              page.drawText('X', { x: COL_X[4] + 9, y: rowY, size: 7, font: fontBold, color: black });
            }
            if (fi.condition === 'NUEVO') {
              page.drawText('X', { x: COL_X[5] + 9, y: rowY, size: 7, font: fontBold, color: black });
            }
            drawRightText(page, fi.unitPrice.toFixed(2), COL_X[6] + COLS_W[6] - 2, rowY, 6, font, black);
            drawRightText(page, fi.total.toFixed(2),     COL_X[7] + COLS_W[7] - 2, rowY, 6, font, black);
            if (drawTaxColumn) {
              drawRightText(page, (fi.taxAmount || 0).toFixed(2), COL_X[8] + COLS_W[8] - 2, rowY, 6, font, black);
            }
            pageSubtotal += fi.total;
          }
        } else if (row.type === 'pkg_subtotal') {
          drawRightText(page, row.total.toFixed(2), COL_X[7] + COLS_W[7] - 2, rowY, 7, fontBold, black);
          page.drawLine({
            start: { x: MARGIN, y: rowY - 3 },
            end:   { x: PAGE_W - MARGIN, y: rowY - 3 },
            thickness: 0.2, color: gray,
          });
          rowY -= ROW_H;
          continue;
        } else {
          page.drawText(String(row.qty), {
            x: COL_X[2] + 2, y: rowY, size: 6, font, color: black,
          });
          page.drawText(ajustarDescripcion(row.description, font, 6, COLS_W[3] - 4), {
            x: COL_X[3] + 2, y: rowY, size: 6, font, color: black,
          });
          if (row.condition !== 'NUEVO') {
            page.drawText('X', { x: COL_X[4] + 9, y: rowY, size: 7, font: fontBold, color: black });
          }
          if (row.condition === 'NUEVO') {
            page.drawText('X', { x: COL_X[5] + 9, y: rowY, size: 7, font: fontBold, color: black });
          }
          drawRightText(page, row.unitPrice.toFixed(2), COL_X[6] + COLS_W[6] - 2, rowY, 6, font, black);
          drawRightText(page, row.total.toFixed(2),     COL_X[7] + COLS_W[7] - 2, rowY, 6, font, black);
          if (drawTaxColumn) {
            drawRightText(page, (row.taxAmount || 0).toFixed(2), COL_X[8] + COLS_W[8] - 2, rowY, 6, font, black);
          }
          pageSubtotal += row.total;
        }

        if (row.type !== 'pkg_subtotal') {
          page.drawLine({
            start: { x: MARGIN, y: rowY - 3 },
            end:   { x: PAGE_W - MARGIN, y: rowY - 3 },
            thickness: 0.2, color: gray,
          });
          rowY -= ROW_H;
        }
      }

      // Pad with blank rows
      const blankRowsNeeded = MAX_ROWS_PER_PAGE - pageRows.length;
      for (let b = 0; b < blankRowsNeeded; b++) {
        page.drawLine({
          start: { x: MARGIN, y: rowY - 3 },
          end:   { x: PAGE_W - MARGIN, y: rowY - 3 },
          thickness: 0.2, color: gray,
        });
        rowY -= ROW_H;
      }

      // Subtotal row
      const subtotalRowY      = rowY;
      const subtotalTopBorderY = subtotalRowY + 8;
      drawRightText(page, 'SUBTOTAL USD$:', COL_X[7] - 2, subtotalRowY, 7, fontBold, black);
      drawRightText(page, pageSubtotal.toFixed(2), COL_X[7] + COLS_W[7] - 2, subtotalRowY, 7, fontBold, black);
      page.drawLine({
        start: { x: MARGIN, y: subtotalTopBorderY },
        end:   { x: PAGE_W - MARGIN, y: subtotalTopBorderY },
        thickness: 0.5, color: black,
      });
      const subtotalBottomY = subtotalRowY - 4;
      page.drawLine({
        start: { x: MARGIN, y: subtotalBottomY },
        end:   { x: PAGE_W - MARGIN, y: subtotalBottomY },
        thickness: 0.5, color: black,
      });

      let tableBottomY = subtotalBottomY;

      // Total row (last page only)
      if (isLastPage) {
        const totalRowY = subtotalRowY - ROW_H;
        drawRightText(page, 'TOTAL USD$:', COL_X[7] - 2, totalRowY, 8, fontBold, black);
        drawRightText(page, grandTotal.toFixed(2), COL_X[7] + COLS_W[7] - 2, totalRowY, 8, fontBold, black);
        drawRightText(page, '0.00', COL_X[8] + COLS_W[8] - 2, totalRowY, 7, font, black);
        const totalBottomY = totalRowY - 4;
        page.drawLine({
          start: { x: MARGIN, y: totalBottomY },
          end:   { x: PAGE_W - MARGIN, y: totalBottomY },
          thickness: 0.5, color: black,
        });
        tableBottomY = totalBottomY;
      }

      // Vertical column separators
      const tableTopY = COL_HDR_Y + 10;
      for (let ci = 0; ci <= COLS_W.length; ci++) {
        const vx     = ci < COLS_W.length ? COL_X[ci] : PAGE_W - MARGIN;
        const startY = (ci === 5) ? (SUB_HDR_Y + 6) : tableTopY;
        page.drawLine({
          start: { x: vx, y: startY },
          end:   { x: vx, y: subtotalTopBorderY },
          thickness: 0.3, color: darkGray,
        });
      }

      const segBIndices = [0, 7, 8, COLS_W.length];
      for (const ci of segBIndices) {
        const vx = ci < COLS_W.length ? COL_X[ci] : PAGE_W - MARGIN;
        page.drawLine({
          start: { x: vx, y: subtotalTopBorderY },
          end:   { x: vx, y: tableBottomY },
          thickness: 0.3, color: darkGray,
        });
      }

      // Footer: Gestor + Firma on every page
      const footerY   = 55;
      const sigLineLen = 140;

      page.drawLine({
        start: { x: MARGIN, y: footerY },
        end:   { x: MARGIN + sigLineLen, y: footerY },
        thickness: 0.6, color: black,
      });
      page.drawText(gestorName.slice(0, 30), {
        x: MARGIN, y: footerY - 10, size: 7, font: fontBold, color: black,
      });
      page.drawText('Gestor de Encomiendas', {
        x: MARGIN, y: footerY - 20, size: 6, font, color: black,
      });

      const rightSigX = PAGE_W - MARGIN - sigLineLen;
      page.drawLine({
        start: { x: rightSigX, y: footerY },
        end:   { x: PAGE_W - MARGIN, y: footerY },
        thickness: 0.6, color: black,
      });
      const firmaW = fontBold.widthOfTextAtSize('Firma', 6);
      page.drawText('Firma', {
        x: rightSigX + (sigLineLen - firmaW) / 2,
        y: footerY - 10, size: 6, font: fontBold, color: black,
      });
    }

    const pdfBytes = await pdfDoc.save();
    console.log('[Anexo] Generated:', logLabel, 'filtered packages:', filteredPackages.length);

    return pdfBytes;

  } catch (err) {
    console.error('[Anexo split] renderAnexoPdf error:', err);
    throw err;
  }
}

// ── Main PDF generator ────────────────────────────────────────────────────────
// Unchanged public contract: (manifestId, supabaseClient, opts) → bytes when
// returnBytes is set, otherwise a browser download.
export async function generateAnexoPDF(
  manifestId,
  supabaseClient,
  { returnBytes = false, skipSeqs = [], onlySeq = null } = {}
) {
  try {
    const loaded = await _loadAndSortAnexo(manifestId, supabaseClient);
    const pdfBytes = await renderAnexoPdf(loaded, {
      skipSeqs,
      onlySeq,
      logLabel: manifestId,
    });

    if (returnBytes) {
      return pdfBytes;
    }

    // Filename varies by mode
    const effectiveSkipSeqs = onlySeq !== null ? [] : skipSeqs;
    const today = new Date().toISOString().slice(0, 10);
    let downloadFilename;
    if (onlySeq !== null) {
      downloadFilename = `Anexo_Individual_${onlySeq}_${manifestId}_${today}.pdf`;
    } else if (effectiveSkipSeqs.length > 0) {
      downloadFilename = `Anexo_Principal_${manifestId}_${today}.pdf`;
    } else {
      downloadFilename = `Anexo_${manifestId}_${today}.pdf`;
    }

    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = downloadFilename;
    a.click();
    URL.revokeObjectURL(url);

  } catch (err) {
    console.error('[Anexo split] generateAnexoPDF error:', err);
    throw err;
  }
}
